import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Dialog } from '@sas/nova-commons/components/Dialog';
import { Icon } from '@sas/nova-commons/components/Icon';
import { Label } from '@sas/nova-commons/components/Label';
import { Flow } from '@sas/nova-commons/components/Layouts/Flow';
import { SearchField } from '@sas/nova-commons/components/SearchField';
import { TabBar } from '@sas/nova-commons/components/TabBar';
import { Text } from '@sas/nova-commons/components/Text';
import { useParentSize } from '@sas/nova-commons/hooks/useParentSize';
import { TreeTable, useTreeTableState } from '@sas/nova-commons/components/TreeTable';
import { BusyStateIndicator } from '@sas/nova-commons/components/BusyStateIndicator';
import { Item, List } from '@sas/nova-commons/components/List';
import { DualSelector } from '@sas/nova-commons/components/DualSelector';
import { renderInlineCssVars, useDesignTokens } from '@sas/nova-commons/theme';
import { Alert, Behaviors, BusinessCompany, Category, Contract, CreditCards, Currency, Dashboard, Education, Employer, Government, Home, Image, Household, LinesPlot, Matrix, Measure, MedicalConditions, Police, Poverty, UserGroup, UserGroupLimit, Information, Date, Folder, } from '@sas/react-svg';
import cx from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useI18n } from '../../../../locale/I18nContext';
import { ObjectSearchState, } from '../../../../store/objectSearch/ObjectSearchState';
import { selectObjectSearchState } from '../../../../store/selectors';
import { useBusyStateAnimationTokens } from '../../../utils/busyStateIndicatorTokens';
import './DemographicsSelectorDialog.css';
import cn from './DemographicsSelectorDialog.css.json';
/**
 * Returns the icon for a given {GeoEnrichmentCategoryType}
 */
const getIconForCategory = (category) => {
    switch (category) {
        case 'usergroup':
            return UserGroup;
        case 'income':
            return Currency;
        case 'age':
            return Date;
        case 'households':
            return Household;
        case 'housing':
            return Home;
        case 'health':
            return MedicalConditions;
        case 'education':
            return Education;
        case 'business':
            return BusinessCompany;
        case 'race':
            return UserGroupLimit;
        case 'spending':
            return CreditCards;
        case 'behaviors':
            return Behaviors;
        case 'jobs':
            return Employer;
        case 'poverty':
            return Poverty;
        case 'maritalstatus':
            return Contract;
        case 'tapestry':
            return Matrix;
        case 'atrisk':
            return Alert;
        case 'keyfacts':
            return Dashboard;
        case 'supplyanddemand':
            return LinesPlot;
        case 'policy':
            return Government;
        case 'landscape':
            return Image;
        case 'population':
            return UserGroup;
        case 'crime':
            return Police;
    }
    return undefined;
};
const filterValueContains = (value, searchValue) => {
    return value.toLowerCase().includes(searchValue.toLowerCase());
};
const getNodeById = (id, data) => {
    const index = data.findIndex((node) => node.subNodes.map((s) => s.id).includes(id));
    return index !== -1 ? data[index] : null;
};
const getChildNodes = (key, forData) => {
    const index = forData.findIndex((value) => {
        return value.key === key;
    });
    const data = forData[index];
    if (!data || !data.node.hasChildren)
        return [];
    const nodes = [];
    let nextLevel = data.node.level + 1;
    for (let rowIndex = index + 1; rowIndex < forData.length; rowIndex++) {
        const { level, hasChildren } = forData[rowIndex].node;
        if (level === nextLevel)
            nodes.push(forData[rowIndex]);
        if (hasChildren)
            nextLevel++;
    }
    return nodes;
};
const getChildNodesRowKey = (key, forData) => {
    return getChildNodes(key, forData).map((node) => node.key);
};
const normalizedData = (expNodes, forData, searchValue) => {
    const newData = [];
    let count = 0;
    for (let rowIndex = 0; rowIndex < forData.length; rowIndex++) {
        const rowData = forData[rowIndex];
        const { hasChildren } = rowData.node;
        let matchesSearch = filterValueContains(rowData.node.text, searchValue);
        if (!matchesSearch && hasChildren) {
            //check to see if any children match
            let index = rowIndex + 1;
            const size = forData.length;
            let next = index < size ? forData[index] : null;
            while (!matchesSearch && next && index <= size && !next.node.hasChildren) {
                matchesSearch = filterValueContains(next.node.text, searchValue);
                index++;
                next = index < size ? forData[index] : null;
            }
        }
        if (matchesSearch) {
            const isExpanded = rowData.subNodes.some((s) => expNodes.has(s.id));
            newData[count] = Object.assign({}, rowData);
            count++;
            if (!isExpanded && hasChildren) {
                const children = getChildNodesRowKey(rowData.key, forData);
                rowIndex += children.length;
            }
        }
    }
    return newData;
};
export const DemographicsSelectorDialogBase = (props) => {
    var _a;
    const localize = useI18n();
    const dt = useDesignTokens();
    // local states
    const item = useMemo(() => {
        var _a;
        return (_a = props.features) === null || _a === void 0 ? void 0 : _a.find((feature, index) => {
            return feature.type === 'area' && index === props.featureIndex;
        });
    }, [props.features, props.featureIndex]);
    const [isFormValid, setIsFormValid] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [selectedTabIndex, setSelectedTabIndex] = useState(0);
    const [filterValue, setFilterValue] = useState('');
    const [setRef, dimensions] = useParentSize();
    const [selectedNodeIds, setSelectedNodeIds] = useState(((_a = item === null || item === void 0 ? void 0 : item.demographics) === null || _a === void 0 ? void 0 : _a.selectedMeasures) || []);
    const treeTableState = useTreeTableState(new Set([]));
    const { onExpand, setSelectRows, setExpandedNodes } = treeTableState;
    const treeTableAPIRef = useRef(null);
    const [basicTreeTableData, setBasicTreeTableData] = useState([]);
    const [treeDualSelectorSourceData, setTreeDualSelectorSourceData] = useState([]);
    const [treeDualSelectorTargetData, setTreeDualSelectorTargetData] = useState([]);
    const [numberOfRows, setNumberOfRows] = useState(basicTreeTableData.length);
    const [displayedData, setDisplayedData] = useState(() => normalizedData(treeTableState.expandedNodes, basicTreeTableData, filterValue));
    const treeTableColumns = [
        { key: 'node', label: '' },
        { key: 'info', label: '', width: 32 },
    ];
    const updateTreeModel = useCallback((updateExpandState) => {
        const data = [];
        const dualSourceData = [];
        const dualTargetData = new Map();
        if (props.demographicCategories) {
            const basicRawData = {};
            let expandCategory = true;
            let counter = 0;
            const processCategory = (category, categoryIndex, level) => {
                var _a, _b;
                // create dual selector folder node
                dualSourceData.push(Object.assign(Object.assign({ id: `c${counter++}`, text: category.label }, (expandCategory && { expanded: true })), { hasChildren: true, level, icon: Object.assign(Object.assign({}, (getIconForCategory(category.name) || Folder)), { tooltipKey: undefined }), infoText: category.description }));
                (_a = category.variables) === null || _a === void 0 ? void 0 : _a.forEach((variable) => {
                    var _a;
                    if (expandCategory) {
                        expandCategory = false;
                    }
                    // create dual selector variable node unless it's already selected (in the target list)
                    const treeNode = {
                        id: variable.name,
                        text: variable.label,
                        icon: variable.type === 'decimal' ? Measure : Category,
                        level: level + 1,
                        infoText: variable.description,
                        isBasicCategory: variable.basicCategory !== undefined,
                    };
                    selectedNodeIds.includes(variable.name)
                        ? dualTargetData.set(variable.name, treeNode)
                        : dualSourceData.push(treeNode);
                    // basic tree nodes
                    if (variable.basicCategory) {
                        if (!basicRawData[variable.basicCategory]) {
                            basicRawData[variable.basicCategory] = {};
                        }
                        if (variable.subGroup) {
                            const subNodes = ((_a = basicRawData[variable.basicCategory][variable.subGroup]) === null || _a === void 0 ? void 0 : _a.subNodes) || [];
                            if (!subNodes.map((s) => s.id).includes(variable.name)) {
                                subNodes.push({
                                    id: variable.name,
                                    text: variable.label,
                                });
                            }
                            basicRawData[variable.basicCategory][variable.subGroup] = {
                                node: {
                                    id: variable.subGroup,
                                    text: variable.subGroup,
                                    hasChildren: false,
                                    icon: variable.type === 'decimal' ? Measure : Category,
                                    level: 1,
                                },
                                subNodes,
                                key: `row-${variable.subGroup}`,
                                data: [],
                            };
                        }
                        else {
                            basicRawData[variable.basicCategory][variable.name] = {
                                node: {
                                    id: variable.name,
                                    text: variable.label,
                                    hasChildren: false,
                                    icon: variable.type === 'decimal' ? Measure : Category,
                                    level: 1,
                                    description: variable.description,
                                },
                                subNodes: [
                                    {
                                        id: variable.name,
                                        text: variable.label,
                                    },
                                ],
                                key: `row-${variable.name}`,
                                data: [variable.description],
                            };
                        }
                    }
                });
                (_b = category.categories) === null || _b === void 0 ? void 0 : _b.forEach((subcategory, index) => processCategory(subcategory, categoryIndex === 0 ? index : -1, level + 1));
            };
            // build tree nodes for the basic tab
            props.demographicCategories.forEach((category, index) => processCategory(category, index, 0));
            // convert to tree table data
            Object.keys(basicRawData).forEach((category) => {
                // group
                data.push({
                    node: {
                        id: category,
                        text: 
                        // @ts-ignore (basicCategory is of type GeoEnrichmentCategoryType and each category is in the resource file)
                        localize({ key: `DemographicsSelectorDialog.category.${category}.txt` }),
                        icon: getIconForCategory(category),
                        hasChildren: true,
                        level: 0,
                    },
                    subNodes: [
                        {
                            id: category,
                            text: '',
                        },
                    ],
                    key: `row-${category}`,
                    data: [''],
                });
                data.push(...Object.values(basicRawData[category]));
            });
        }
        // basic tree
        setBasicTreeTableData(data);
        const selectedRows = data
            .filter((v) => v.subNodes.some((s) => selectedNodeIds.includes(s.id)))
            .map((node) => node.key);
        // expand branches with selected nodes by default
        if (updateExpandState) {
            const expandedNodes = new Set();
            data.forEach((node) => {
                if (node.node.hasChildren &&
                    getChildNodesRowKey(node.key, data).some((childRowKey) => selectedRows.includes(childRowKey))) {
                    expandedNodes.add(node.node.id);
                }
            });
            setExpandedNodes(expandedNodes);
        }
        setSelectRows({
            selectedRows: new Set(selectedRows),
        });
        // dual selector
        setTreeDualSelectorSourceData(dualSourceData);
        setTreeDualSelectorTargetData(Array.from(dualTargetData.values()).sort((a, b) => selectedNodeIds.indexOf(a.id) > selectedNodeIds.indexOf(b.id) ? 1 : -1));
        setIsBusy(false);
    }, [props.demographicCategories, localize, selectedNodeIds, setExpandedNodes, setSelectRows]);
    // validate the form entries
    useEffect(() => {
        // basic form validation
        let isValid = false;
        if (selectedNodeIds.length > 0) {
            isValid = true;
        }
        setIsFormValid(isValid);
    }, [selectedNodeIds]);
    // update tree models on first render
    const refFirstRender = useRef(true);
    useEffect(() => {
        if (refFirstRender.current && props.demographicCategories) {
            updateTreeModel(true);
            refFirstRender.current = false;
        }
    }, [updateTreeModel, props.demographicCategories]);
    // update tree on node expansion or filter change
    useEffect(() => {
        const newDisplayData = normalizedData(treeTableState.expandedNodes, basicTreeTableData, filterValue);
        setDisplayedData(newDisplayData);
        setNumberOfRows(newDisplayData.length);
    }, [treeTableState.expandedNodes, basicTreeTableData, filterValue]);
    const spacingTokens = renderInlineCssVars({ 'spacing-m': dt.tokens['space-base-m'] }, 'demographics');
    const treeStyles = renderInlineCssVars({
        'table-scroll-bar-space': '-.25rem',
        'tree-hover-bg': 'transparent',
    });
    const busyAnimationTokens = useBusyStateAnimationTokens();
    return (_jsx(Dialog, Object.assign({ className: cn.demographicsDialog, style: Object.assign(Object.assign({}, spacingTokens), { minHeight: '20rem', minWidth: '30rem' }), isOpen: props.isOpen, title: localize({ key: 'DemographicsSelectorDialog.title.txt' }), firstAction: {
            text: localize({ key: 'DemographicsSelectorDialog.ok.txt' }),
            disabled: !isFormValid,
            isDefault: true,
            onPress: () => {
                // save measure selection
                props.actions.setSelectedDemographicMeasures(props.id, {
                    index: props.featureIndex,
                    selectedMeasures: Array.from(selectedNodeIds).filter((id) => { var _a; return !((_a = getNodeById(id, basicTreeTableData)) === null || _a === void 0 ? void 0 : _a.node.hasChildren); }),
                });
                props.closeDialog('ok');
            },
        }, dismissAction: {
            text: localize({ key: 'DemographicsSelectorDialog.cancel.txt' }),
            onPress: () => {
                props.closeDialog('cancel');
            },
        }, resizable: true }, { children: _jsxs(Flow, Object.assign({ spacing: 's', orientation: 'vertical', className: cn.formContainer, ref: setRef }, { children: [_jsx(TabBar, { visualStyle: "standard", selectedIndex: selectedTabIndex, onItemSelected: ({ index }) => {
                        setSelectedTabIndex(index);
                        updateTreeModel();
                    }, items: [
                        { label: localize({ key: 'DemographicsSelectorDialog.tab.basic.txt' }), altId: 'one' },
                        {
                            label: localize({ key: 'DemographicsSelectorDialog.tab.advanced.txt' }),
                            altId: 'two',
                        },
                        {
                            label: localize({ key: 'DemographicsSelectorDialog.tab.recent.txt' }),
                            altId: 'three',
                        },
                    ] }), _jsx(BusyStateIndicator, Object.assign({ animationTokens: busyAnimationTokens, active: isBusy, fillParentContainer: true, buttonText: localize({ key: 'DemographicsSelectorDialog.cancel.txt' }), onButtonPress: () => setIsBusy(false) }, { children: _jsxs(Flow, Object.assign({ spacing: 's', orientation: 'vertical' }, { children: [_jsx(Label, Object.assign({ className: cn.formHeader }, { children: localize({ key: 'DemographicsSelectorDialog.categories.txt' }) })), (selectedTabIndex === 0 || selectedTabIndex === 2) && (_jsx(SearchField, { className: cn.formField, filterMode: true, value: filterValue, onChange: (e) => setFilterValue(e.value), numberOfResults: filterValue ? numberOfRows : null })), _jsxs("div", Object.assign({ className: cx(cn.formField, cn.formTree) }, { children: [dimensions && selectedTabIndex === 0 && (_jsx(TreeTable, Object.assign({ style: treeStyles, rowCount: numberOfRows, columns: treeTableColumns, treeTableAPIRef: treeTableAPIRef, expandToFillWidth: true, height: dimensions.height - 120, columnHeadersVisible: false, cellBordersVisible: false, selectionMode: 'parentChild', renderCellData: ({ rowIndex, columnKey, disabled }) => {
                                            const index = treeTableColumns === null || treeTableColumns === void 0 ? void 0 : treeTableColumns.findIndex((col) => col.key === columnKey);
                                            const columnValue = displayedData.length > rowIndex
                                                ? displayedData[rowIndex].data[index - 1]
                                                : '';
                                            return columnKey === 'info' && (columnValue === null || columnValue === void 0 ? void 0 : columnValue.length) > 0 ? (_jsx(Icon, { icon: Information, tooltip: columnValue, onClick: (event) => {
                                                    event.preventDefault();
                                                    event.stopPropagation();
                                                }, disabled: disabled })) : (_jsx(Text, Object.assign({ wrapText: false, disabled: disabled }, { children: columnValue })));
                                        }, getDataNode: (rowIndex) => {
                                            return displayedData[rowIndex] ? displayedData[rowIndex].node : undefined;
                                        }, getRowKey: (rowIndex) => {
                                            if (rowIndex >= displayedData.length || rowIndex < 0)
                                                return '';
                                            return displayedData[rowIndex].key;
                                        }, getRowIndex: (rowKey) => {
                                            return displayedData.findIndex((value) => {
                                                return value.key === rowKey;
                                            });
                                        }, getChildNodesRowKey: (rowKey) => {
                                            return getChildNodesRowKey(rowKey, basicTreeTableData);
                                        }, rowHeight: () => 28, onSelect: (selectedRows) => {
                                            const nodeIds = new Set();
                                            if (selectedRows) {
                                                selectedRows.forEach((rowKey) => {
                                                    const rowIndex = basicTreeTableData.findIndex((value) => value.key === rowKey);
                                                    if (rowIndex >= 0) {
                                                        const rowNode = basicTreeTableData[rowIndex];
                                                        if (rowNode.node.hasChildren) {
                                                            const childNodes = getChildNodes(rowKey, basicTreeTableData);
                                                            childNodes.forEach((child) => child.subNodes.map((s) => s.id).forEach(nodeIds.add, nodeIds));
                                                        }
                                                        rowNode.subNodes.map((s) => s.id).forEach(nodeIds.add, nodeIds);
                                                    }
                                                });
                                            }
                                            setSelectedNodeIds(selectedNodeIds
                                                .filter((nodeId) => {
                                                // maintain any previous non-basic-category selected node ids
                                                const node = treeDualSelectorTargetData.find((treeItem) => treeItem.id === nodeId);
                                                return node && !node.isBasicCategory;
                                            })
                                                .concat(Array.from(nodeIds)));
                                        }, getParentsRowKey: (key) => {
                                            const index = displayedData.findIndex((value) => value.key === key);
                                            const keys = [];
                                            const data = displayedData[index];
                                            let parentLevel = (data === null || data === void 0 ? void 0 : data.node.level) - 1;
                                            for (let rowIndex = index; rowIndex >= 0; rowIndex--) {
                                                const { level } = displayedData[rowIndex].node;
                                                if (level === parentLevel) {
                                                    keys.push(displayedData[rowIndex].key);
                                                    --parentLevel;
                                                }
                                            }
                                            return keys;
                                        }, areRowsVisible: (rows) => {
                                            let valid = true;
                                            for (const row of rows) {
                                                const item = displayedData.find((value) => {
                                                    return value.key === row;
                                                });
                                                if (item === undefined) {
                                                    valid = false;
                                                    break;
                                                }
                                            }
                                            return valid;
                                        } }, treeTableState, { onExpand: (node, expanded) => {
                                            const nodes = new Set(treeTableState.expandedNodes);
                                            if (nodes.has(node.id) && !expanded)
                                                nodes.delete(node.id);
                                            else if (!nodes.has(node.id) && expanded)
                                                nodes.add(node.id);
                                            const data = normalizedData(nodes, basicTreeTableData, filterValue);
                                            setDisplayedData(data);
                                            onExpand(node, expanded);
                                            setNumberOfRows(data.length);
                                        } }))), dimensions && selectedTabIndex === 1 && (_jsx(DualSelector, { type: "tree", mode: "move", disableAddButton: selectedNodeIds.length >= 100, style: Object.assign(Object.assign({}, treeStyles), { height: `${dimensions.height - 120}px` }), dataFieldKeys: ['text'], hideAddAllButton: true, sourceAttribute: [
                                            {
                                                hideSearch: false,
                                                data: treeDualSelectorSourceData,
                                            },
                                        ], targetAttribute: [
                                            {
                                                data: treeDualSelectorTargetData,
                                            },
                                        ], onTargetChange: (selectedItems) => {
                                            queueMicrotask(() => setSelectedNodeIds(selectedItems.map((item) => item.id)));
                                        } })), dimensions &&
                                        selectedTabIndex === 2 &&
                                        (() => {
                                            var _a, _b;
                                            const recentMeasures = [];
                                            (_b = (_a = item === null || item === void 0 ? void 0 : item.demographics) === null || _a === void 0 ? void 0 : _a.recentMeasures) === null || _b === void 0 ? void 0 : _b.forEach((measureId) => {
                                                const node = treeDualSelectorSourceData
                                                    .concat(treeDualSelectorTargetData)
                                                    .find((n) => n.id === measureId);
                                                if (!!node && (!filterValue || filterValueContains(node.text, filterValue))) {
                                                    recentMeasures.push(node);
                                                }
                                            });
                                            return (_jsx(List, { style: treeStyles, rowCount: recentMeasures.length, width: dimensions.width, height: dimensions.height - 120, itemHeight: () => 'single', selectionMode: 'desktop', itemColumn: {
                                                    key: 'mainContent',
                                                    item: (index) => {
                                                        var _a;
                                                        return (_jsx(Item, { title: (_a = recentMeasures[index]) === null || _a === void 0 ? void 0 : _a.text, icon: { icon: Measure } }));
                                                    },
                                                }, iconColumns: [
                                                    {
                                                        key: 'icon',
                                                        cellIcon: (index) => {
                                                            return recentMeasures[index].infoText
                                                                ? {
                                                                    icon: Information,
                                                                    tooltip: recentMeasures[index].infoText,
                                                                }
                                                                : null;
                                                        },
                                                    },
                                                ], onSelect: (selectedRows) => {
                                                    if (!selectedRows) {
                                                        return;
                                                    }
                                                    const nodeIds = Array.from(selectedRows).map((index) => recentMeasures[parseInt(index)].id);
                                                    setSelectedNodeIds(nodeIds);
                                                } }));
                                        })()] }))] })) }))] })) })));
};
export const DemographicsSelectorDialog = connect((state, props) => {
    var _a;
    const { features, demographicCountries, demographicCategories } = (_a = selectObjectSearchState(state, props.id)) !== null && _a !== void 0 ? _a : {};
    return {
        features,
        demographicCountries,
        demographicCategories,
    };
}, (dispatch) => ({
    actions: bindActionCreators(ObjectSearchState.actions, dispatch),
}))(DemographicsSelectorDialogBase);
